import { createReducer, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';

import { setIsAuthenticated, resetAuthentication, resetAll, setRunSetup, setStudy, resetStudy, setPanel, setpatientFileMetadata, setcalFileMetadata, setcontrolFileMetadata, runCalculation, runCalculationSuccess, loadCalLotSuccess, loadQcLotSuccess, setSelectedCalcAssayLotRunId, setSelectedSampleAssayLotRunId, setCalcFailedSamplesMode, setSampleFailedSamplesMode, setAuthToken, setMeanBiasAndSlope, setAdjustedCalLots, resetSelectedStudyAndPanel, setCalculateAdjustedEnabled, setSelectedControlQcAnalysisLotRunId, setSelectedControlCurveLotRunId, setActiveAssayTab, saveStudySuccess, setSelectedAssignmentLotRunId, setComments, setRunDataPanels, setProcessingStatus, runGetCalculationCsvSuccess, setRunSamples, patchReportContentItems, loadSampleDataSuccess, beginSampleDataLoad, loadSampleDataFailure, deleteCalLot, deleteQcLot, setLoadStudyError, setAssaySummaryColumnSelection, setAssayDetailsColumnSelection, setLastCalculationCache, setCalAdjustedDisplayOrder, setAllowedAdjustmentFactor, setHasNewInstrumentUploads, setAuditTrail, setCoaDetails, setCalibratorCoaDetails, setControlCoaDetails, setRmqcAssayCount, markAdjustedAssignmentSeeded } from './actions';
import { AppState } from './store';
import { Study } from '../../shared/models/study.model';
import { CalculationResponse } from '../../shared/models/result.model';
import { checkCalculateStatus, createAdjustedCalLots, enableCalculate, extractCalAdjustmentCriteria, getConcentrationsByRegion } from '../../shared/utils/helper';
import { buildInstrumentReportSeeds, alignRunSampleLots, pruneRunSamplesToMetadata } from '../../shared/utils/run-info.utils';

const shouldDefaultCalculateAverages = (runType: string | undefined | null): boolean => {
  const normalized = (runType ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();
  return normalized === 'calibratorassignment' ||
    normalized === 'controlassignment' ||
    normalized === 'calibratoradjustment' ||
    normalized === 'calibratormatchkit' ||
    normalized === 'controlmatchkit';
};

const shouldDefaultSkipMissingSamples = (runType: string | undefined | null): boolean => {
  const normalized = (runType ?? '').trim().replace(/[\s_-]+/g, '').toLowerCase();
  return normalized === 'controlmatchkit' || normalized === 'controlassignment';
};

type LoadedLot = { valueAssignmentVersion?: number | null; assays?: unknown[]; vials?: unknown[] };

/**
 * Puts a loaded lot into the list: over the same lot and version, else over a detail-less draft
 * stub of the same lot number, else at the end.
 *
 * The stub fallback is what stops a restored draft listing a lot twice, since the stub's saved
 * version need not match the version the API sends back.
 */
function upsertLot<T extends LoadedLot>(lots: T[], loaded: T, lotNumberOf: (lot: T) => string): T[] {
  const sameLot = (lot: T) => lotNumberOf(lot) === lotNumberOf(loaded);
  const isStub = (lot: T) => !(lot.assays?.length) && !(lot.vials?.length);

  let index = lots.findIndex(lot =>
    sameLot(lot) && (lot.valueAssignmentVersion ?? null) === (loaded.valueAssignmentVersion ?? null));
  if (index < 0) {
    index = lots.findIndex(lot => sameLot(lot) && isStub(lot));
  }

  return index >= 0
    ? [...lots.slice(0, index), loaded, ...lots.slice(index + 1)]
    : [...lots, loaded];
}

/**
 * The initial state for the application.
 * Sets default values for user authentication, study configuration,
 * panel metadata, file uploads, calculation settings, and study run data.
 *
 * @type {AppState}
 */
export const initialState: AppState = {
  isProcessing: false,
  isAuthenticated: false,
  authToken: '',
  existingStudy: false,
  loggedInUser: '',
  loggedInUserEmail: '',
  appVersion: '',
  isSuperUser: false,
  selectedStudy: {
    studyId: '',
    studyName: '',
    runType: '', // PhaseTypeEnum.RMQC
    panelSpecs: null,
    testSpec: '', // PhaseTypeEnum.RMQC
    assignSpec: '', // AssignSpecTypeEnum.MiniBlend
    refKitLot: '',
    patientFileMetadata: [],
    calFileMetadata: [],
    controlFileMetadata: [],
    runSamples: [],
     runSampleLots: [],
    reportContentItems: {
      reportContentItems: [],
      variableColumns: []
    },
    selectedCalcReportContentItemId: null,
    selectedSampleReportContentItemId: null,
    calcFailedSamplesMode: false,
    sampleFailedSamplesMode: false,
    calLots: [],
    adjustedCalLots: {
      originalCalLot: [],
      adjustedCalLot: [],
      valueAssignment: []
    },
    calAdjustedDisplayOrder: {
      kitLotOrder: [],
      regionOrderByKitLot: {}
    },
    meanBiasAndSlope: {},
    qcLots: [],
    kitLots: [],
    assayNames: [],
    beadRegions: [],
    calculationResponse: null, // mockData.calculationResponse,
    calculationAdjustedResponse: null, // mockData.calculationResponse,
    sampleDataOriginal: [],
    sampleDataAdjusted: [],
    sampleDataFailuresOriginal: [],
    sampleDataFailuresAdjusted: [],
    assaySummaryColumnSelection: [],
    assayDetailsColumnSelection: [],
    runDataPanels: null,
    use_legacy_4pl: false,
    optimize_adjustment: false,
    allowed_adjustment_factor: null
  },
  areStudyChangesPending: false,
  panelName: '',
  isFirstColumnReferenceLot: true,
  isCalculateEnabled: false,
  isCalculateAdjustedEnabled: false,
  calculationStatus: 'NA',
  calAdjustmentCriteria: [],
  loadStudyError: null,
  preparedInstrumentReportSeeds: [],
  preparedRunSampleLots: [],
  sampleDataFresh: false,
  sampleDataLoading: false,
  sampleDataPendingLoads: null,
  lastCalculationId: null,
  lastCalculationType: null,
  lastUnadjustedCalculationId: null,
  lastAdjustedCalculationId: null,
  lastSavedComment: null,
  hasNewInstrumentUploads: false,
  adjustedCalibratorVisited: false,
  hasSeededAdjustedAssignment: false,
  calibratorCoaDetails: null,
  controlCoaDetails: null,
  studyInstanceId: 0
};

/**
 * Main application state reducer using NgRx `createReducer`.
 * Handles authentication, study loading and modification, panel configuration, file metadata, calculation status, and more.
 */
export const appStateReducer = createReducer(
  initialState,
  /** Sets authentication status and logged-in user. */
  on(setIsAuthenticated, (state, { isAuthenticated, user, userEmail, appVersion, isSuperUser }): AppState => ({
    ...state,
    isAuthenticated: isAuthenticated,
    loggedInUser: user,
    loggedInUserEmail: isAuthenticated ? (userEmail ?? '') : '',
    appVersion: appVersion ?? state.appVersion,
    isSuperUser: isAuthenticated ? (isSuperUser ?? false) : false
  })),
  /** Stores the authentication token. */
  on(setAuthToken, (state, { authToken }): AppState => ({
    ...state,
    authToken: authToken
  })),
  /** Resets authentication flags (used on logout). */
  on(resetAuthentication, (state): AppState => ({
    ...state,
    isAuthenticated: false,
    isSuperUser: false
  })),
  /** Sets the currently selected study and flags it as existing. */
  on(setStudy, (state, { selectedStudy }): AppState => {
    const clonedStudy = cloneDeep(selectedStudy);
    clonedStudy.sampleDataOriginal = clonedStudy.sampleDataOriginal ?? [];
    clonedStudy.sampleDataAdjusted = clonedStudy.sampleDataAdjusted ?? [];
    clonedStudy.sampleDataFailuresOriginal = clonedStudy.sampleDataFailuresOriginal ?? [];
    clonedStudy.sampleDataFailuresAdjusted = clonedStudy.sampleDataFailuresAdjusted ?? [];
    clonedStudy.assaySummaryColumnSelection = clonedStudy.assaySummaryColumnSelection ?? [];
    clonedStudy.assayDetailsColumnSelection = clonedStudy.assayDetailsColumnSelection ?? [];
    clonedStudy.optimize_adjustment = !!clonedStudy.optimize_adjustment;

    if (clonedStudy.calculationAdjustedResponse?.adjustedCalculationResponse != null) {
      clonedStudy.calculationAdjustedResponse = {
        ...clonedStudy.calculationAdjustedResponse,
        calculationResponse: cloneDeep(clonedStudy.calculationAdjustedResponse.adjustedCalculationResponse)
      };
    }

    // `adjustedCalLots` is not persisted on save (SaveStudyRequest omits it); on load the backend
    // reconstructs it keyed by the calibrator lot, which collapses multiple kit lots that share a bead
    // region into a single section. Rebuild it from the calc response so the per-kit-lot grouping matches
    // a from-scratch run. Prefer the original response, but fall back to the adjusted response because a
    // loaded adjusted study has an empty original response (its report items live on the adjusted one).
    const calcResponseForCalLots =
      (clonedStudy.calculationResponse?.calculationResponse?.reportContentItems?.length
        ? clonedStudy.calculationResponse
        : null)
      ?? (clonedStudy.calculationAdjustedResponse?.calculationResponse?.reportContentItems?.length
        ? clonedStudy.calculationAdjustedResponse
        : null);

    const hasAdjustedCalLots = (clonedStudy.adjustedCalLots?.adjustedCalLot?.length ?? 0) > 0;
    if ((clonedStudy.calLots?.length ?? 0) > 0 && (calcResponseForCalLots || !hasAdjustedCalLots)) {
      const originalCalLot = getConcentrationsByRegion(clonedStudy.calLots[0]); // Only take the first imported cal lot
      const baseAdjusted = {
        originalCalLot,
        adjustedCalLot: cloneDeep(originalCalLot),
        valueAssignment: clonedStudy.adjustedCalLots?.valueAssignment ?? []
      };
      const rebuilt = calcResponseForCalLots
        ? createAdjustedCalLots(calcResponseForCalLots, baseAdjusted)
        : baseAdjusted;
      // Preserve the persisted value-assignment arrays; createAdjustedCalLots only carries valueAssignment.
      clonedStudy.adjustedCalLots = {
        ...rebuilt,
        valueAssignment: clonedStudy.adjustedCalLots?.valueAssignment ?? rebuilt.valueAssignment ?? [],
        originalValueAssignment: clonedStudy.adjustedCalLots?.originalValueAssignment ?? []
      };
    }

    const loadedUnadjustedCalculationId = clonedStudy.unadjustedCalculationId ?? null;
    const loadedAdjustedCalculationId = clonedStudy.adjustedCalculationId ?? null;
    const loadedLastCalculationType = loadedAdjustedCalculationId
      ? 'adjusted'
      : (loadedUnadjustedCalculationId ? 'unadjusted' : null);
    const loadedLastCalculationId = loadedAdjustedCalculationId ?? loadedUnadjustedCalculationId ?? null;

    return {
      ...state,
      selectedStudy: clonedStudy,
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      panelName: selectedStudy.panelSpecs?.kitname!,
      // Same response the adjusted cal lots were rebuilt from above, so the two stay in step: a
      // loaded adjusted-only study has an empty original response and would otherwise get kit-lot
      // keyed cal lots with no criteria to go with them.
      calAdjustmentCriteria: extractCalAdjustmentCriteria(calcResponseForCalLots),
      calculationStatus: checkCalculateStatus(selectedStudy.calculationResponse ?? null, selectedStudy.calculationAdjustedResponse ?? null),
      isProcessing: false,
      processingActivity: undefined,
      loadStudyError: null,
      existingStudy: true,
      // *MS
      preparedInstrumentReportSeeds: buildInstrumentReportSeeds(clonedStudy),
      preparedRunSampleLots: cloneDeep(clonedStudy.runSampleLots ?? []),
      sampleDataFresh: (clonedStudy.sampleDataOriginal?.length ?? 0) > 0 ||
        (clonedStudy.sampleDataFailuresOriginal?.length ?? 0) > 0,
      sampleDataLoading: false,
      sampleDataPendingLoads: null,
      lastCalculationId: loadedLastCalculationId,
      lastCalculationType: loadedLastCalculationType,
      lastUnadjustedCalculationId: loadedUnadjustedCalculationId,
      lastAdjustedCalculationId: loadedAdjustedCalculationId,
      lastSavedComment: clonedStudy.comments ?? '',
      hasNewInstrumentUploads: false,
      adjustedCalibratorVisited: false,
      hasSeededAdjustedAssignment: false,
      calibratorCoaDetails: null,
      controlCoaDetails: null,
      areStudyChangesPending: false,
      // A load swaps in a different study, so components keyed on this rebuild.
      studyInstanceId: (state.studyInstanceId ?? 0) + 1
      // *MS
    };
  }),
  /** Resets the selected study to an empty `Study` instance. */
  on(resetStudy, (state): AppState => ({
    ...state,
    selectedStudy: new Study(''),
    existingStudy: false,
    loadStudyError: null,
    sampleDataFresh: false,
    sampleDataLoading: false,
    sampleDataPendingLoads: null,
    lastCalculationId: null,
    lastCalculationType: null,
    lastUnadjustedCalculationId: null,
    lastAdjustedCalculationId: null,
    lastSavedComment: null,
    hasNewInstrumentUploads: false,
    hasSeededAdjustedAssignment: false,
    calibratorCoaDetails: null,
    controlCoaDetails: null,
    areStudyChangesPending: false,
    studyInstanceId: (state.studyInstanceId ?? 0) + 1
  })),
  /** Sets run samples and report content. Updates calculate enabled state. */
  on(setRunSetup, (state, { runSamples, reportContentItemResponse, comments, testSpec, assignSpec, refLot, calculateAverages, useLegacy4pl, optimizeAdjustment, skipMissingSamples }): AppState => {
    // Per-lot sample types that still match Run Setup's previous type are untouched defaults, so they follow the new one.
    const previousSamples = state.selectedStudy.runSamples ?? [];
    const followedLots = cloneDeep(state.selectedStudy.runSampleLots ?? []);
    followedLots.forEach(lot => lot.runSamples?.forEach((lotSample, index) => {
      const before = previousSamples[index];
      const after = runSamples[index];
      if (before && after && before.accessionId === after.accessionId &&
        before.sampleType !== after.sampleType && lotSample.sampleType === before.sampleType) {
        lotSample.sampleType = after.sampleType;
      }
    }));
    const updatedStudy = {
      ...state.selectedStudy,
      runSamples: cloneDeep(runSamples),
      runSampleLots: followedLots,
      reportContentItems: { ...cloneDeep(reportContentItemResponse) },
      comments: comments,
      testSpec: testSpec,
      assignSpec: assignSpec,
      refKitLot: refLot,
      calculate_averages: calculateAverages,
      use_legacy_4pl: useLegacy4pl,
      optimize_adjustment: optimizeAdjustment,
      skip_missing_samples: skipMissingSamples
    };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      preparedRunSampleLots: cloneDeep(followedLots),
      areStudyChangesPending: true
    };
  }),
  /** Sets metadata for patient files, along with related assay and bead information. */
  on(setpatientFileMetadata, (state, { patientFileMetadata, kitLots }): AppState => {
    const updatedStudy = { ...state.selectedStudy, patientFileMetadata: cloneDeep(patientFileMetadata), kitLots: cloneDeep(kitLots) };
    updatedStudy.runSamples = pruneRunSamplesToMetadata(updatedStudy);
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      preparedInstrumentReportSeeds: buildInstrumentReportSeeds(updatedStudy),
      areStudyChangesPending: true
    };
  }),
  on(patchReportContentItems, (state, { changedItems, variableColumns, removedItemIds }): AppState => {
    if (!changedItems?.length && !variableColumns && !(removedItemIds?.length)) {
      return state;
    }
    // *MS Merge only the touched rows so the action payload stays lean and Run Info tabs avoid rehydrating hundreds of untouched records.*MS
    const currentResponse = state.selectedStudy.reportContentItems ?? { variableColumns: [], reportContentItems: [] };
    const removedIdsSet = new Set((removedItemIds ?? []).filter(id => !!id));
    let nextItems = cloneDeep(currentResponse.reportContentItems ?? []);
    if (removedIdsSet.size) {
      nextItems = nextItems.filter(item => !removedIdsSet.has(item.reportContentItemId));
    }
    const indexById = new Map(nextItems.map((item, index) => [item.reportContentItemId, index]));
    changedItems.forEach(item => {
      const clone = cloneDeep(item);
      const id = clone.reportContentItemId;
      if (id && indexById.has(id)) {
        nextItems[indexById.get(id)!] = clone;
      } else {
        nextItems.push(clone);
      }
    });
    const nextResponse = {
      variableColumns: variableColumns ?? currentResponse.variableColumns,
      reportContentItems: nextItems
    };
    const nextRunSampleLots = alignRunSampleLots(state.selectedStudy.runSampleLots, nextItems);
    const nextPreparedRunSampleLots = alignRunSampleLots(state.preparedRunSampleLots, nextItems);
    const updatedStudy = { ...state.selectedStudy, reportContentItems: nextResponse, runSampleLots: nextRunSampleLots };
    return {
      ...state,
      selectedStudy: updatedStudy,
      preparedRunSampleLots: nextPreparedRunSampleLots,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      areStudyChangesPending: true
    };
  }),
  /** Sets calibrator file metadata. */
  on(setcalFileMetadata, (state, { calFileMetadata, kitLots }): AppState => {
    const updatedStudy = { ...state.selectedStudy, calFileMetadata: cloneDeep(calFileMetadata), kitLots: cloneDeep(kitLots) };
    updatedStudy.runSamples = pruneRunSamplesToMetadata(updatedStudy);
    return {
      ...state,
      selectedStudy: updatedStudy,
      // Calibrator files count as instrument data, so they can complete the calculate prerequisites.
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      preparedInstrumentReportSeeds: buildInstrumentReportSeeds(updatedStudy),
      areStudyChangesPending: true
    };
  }),
  /** Sets control file metadata. */
  on(setcontrolFileMetadata, (state, { controlFileMetadata, kitLots }): AppState => {
    const updatedStudy = { ...state.selectedStudy, controlFileMetadata: cloneDeep(controlFileMetadata), kitLots: cloneDeep(kitLots) };
    updatedStudy.runSamples = pruneRunSamplesToMetadata(updatedStudy);
    return {
      ...state,
      selectedStudy: updatedStudy,
      // Control files count as instrument data, so they can complete the calculate prerequisites.
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      preparedInstrumentReportSeeds: buildInstrumentReportSeeds(updatedStudy),
      areStudyChangesPending: true
    };
  }),
  /** Sets the RMQC-only number of assay rows to seed per selected kit lot. */
  on(setRmqcAssayCount, (state, { rmqcAssayCount }): AppState => {
    return {
      ...state,
      selectedStudy: { ...state.selectedStudy, rmqcAssayCount },
      areStudyChangesPending: true
    };
  }),
  on(setRunSamples, (state, { runSampleLots }): AppState => {
    const lotsClone = cloneDeep(runSampleLots);
    const updatedStudy = { ...state.selectedStudy, runSampleLots: lotsClone };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      preparedRunSampleLots: lotsClone,
      areStudyChangesPending: true
    };
  }),
  /** Configures the selected panel including specs and run type. */
  on(setPanel, (state, { panelName, runType, panelSpecs, reportContentItems, assayNames, beadRegions, isRestore }): AppState => {
    const existingCalculateAverages = state.selectedStudy.calculate_averages;
    const calculateAverages = typeof existingCalculateAverages === 'boolean'
      ? existingCalculateAverages
      : shouldDefaultCalculateAverages(runType);
    const existingSkipMissingSamples = state.selectedStudy.skip_missing_samples;
    const skipMissingSamples = typeof existingSkipMissingSamples === 'boolean'
      ? existingSkipMissingSamples
      : shouldDefaultSkipMissingSamples(runType);
    const updatedStudy = {
      ...state.selectedStudy,
      runType: runType,
      panelSpecs: panelSpecs,
      reportContentItems: reportContentItems,
      assayNames: assayNames,
      beadRegions: beadRegions,
      calculate_averages: calculateAverages,
      skip_missing_samples: skipMissingSamples
    };
    const isCalibratorAdjustment = runType.trim().replace(/[\s_-]+/g, '').toLowerCase() === 'calibratoradjustment';
    return {
      ...state,
      panelName: panelName,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, panelName),
      preparedInstrumentReportSeeds: buildInstrumentReportSeeds(updatedStudy),
      ...(isCalibratorAdjustment && { adjustedCalibratorVisited: false }),
      // Load replays setPanel straight after setStudy, so only a user-driven panel change is an edit.
      areStudyChangesPending: isRestore ? state.areStudyChangesPending : true
    };
  }),
  on(runGetCalculationCsvSuccess, (state): AppState => ({
    ...state,    
    isCalculateEnabled: true,
  })),
  /** A calculation in flight holds the processing banner until its success or error clears it. */
  on(runCalculation, (state): AppState => ({
    ...state,
    isProcessing: true,
    processingActivity: 'calculating',
    loadStudyError: null
  })),
  /**  Handles the successful result of a run calculation. Stores original or adjusted responses accordingly. */
  on(runCalculationSuccess, (state, { calculationResponse, calAdjustmentMode, finalValueAssignment }): AppState => {
    const isFinal = calAdjustmentMode === 'Final';
    // After a Final run there is no baseline, so a dual run's unadjusted half restores it.
    const restoredOriginal = calculationResponse.valueAssignment === 'CalibrateAdjustedCalculate'
      && !state.selectedStudy.calculationResponse?.calculationResponse
      && calculationResponse.calculationResponse
      ? { calculationResponse: cloneDeep(calculationResponse.calculationResponse), valueAssignment: 'OriginalCalculate' } as CalculationResponse
      : null;
    const updatedSelectedStudy = {
      ...state.selectedStudy,
      // Final replaces both halves, so drop the baseline to match how a saved Final study loads.
      calculationResponse: isFinal
        ? { calculationResponse: null, valueAssignment: 'OriginalCalculate' } as CalculationResponse
        : calculationResponse.valueAssignment === "OriginalCalculate"
          ? cloneDeep(calculationResponse)
          : restoredOriginal ?? state.selectedStudy.calculationResponse,
      calculationAdjustedResponse: calculationResponse.valueAssignment === "AdjustedCalculate"
        ? cloneDeep(calculationResponse)
        : calculationResponse.valueAssignment === "CalibrateAdjustedCalculate"
          ? { ...cloneDeep(calculationResponse), calculationResponse: cloneDeep(calculationResponse.adjustedCalculationResponse) ?? null } as CalculationResponse
          // A fresh original run invalidates whatever adjustment was computed against the
          // previous baseline, so drop it here rather than leaving it to display against samples
          // it was never computed for until the operator re-runs "Calculate wADJ".
          : calculationResponse.valueAssignment === "OriginalCalculate"
            ? null
            : state.selectedStudy.calculationAdjustedResponse,
      ...(calculationResponse.valueAssignment === "OriginalCalculate" && {
        adjustedCalLots: createAdjustedCalLots(calculationResponse, state.selectedStudy.adjustedCalLots)
      }),
      ...(calAdjustmentMode && { cal_adjustment_mode: calAdjustmentMode }),
      ...(isFinal && {
        adjustedCalLots: {
          ...state.selectedStudy.adjustedCalLots,
          valueAssignment: cloneDeep(finalValueAssignment ?? [])
        },
        sampleDataOriginal: [],
        sampleDataFailuresOriginal: [],
        unadjustedCalculationId: null
      })
    };

    const nextOriginalResponse = updatedSelectedStudy.calculationResponse;
    const nextAdjustedResponse = updatedSelectedStudy.calculationAdjustedResponse;

    return {
      ...state,
      selectedStudy: updatedSelectedStudy,
      // The criteria describe the unadjusted baseline — which kit lot/region combinations are
      // adjustable, and their original bias and slope — so only an original calculate refreshes
      // them. An adjusted response reports post-adjustment stats, and recomputing from it churned
      // (or emptied) the criteria that the adjusted cal lots and the wADJ payload key off.
      calAdjustmentCriteria: calculationResponse.valueAssignment === 'OriginalCalculate'
        ? extractCalAdjustmentCriteria(calculationResponse)
        : state.calAdjustmentCriteria,
      calculationStatus: checkCalculateStatus(nextOriginalResponse, nextAdjustedResponse),
      // A new adjusted response is the only thing that makes a fresh seed due. Keying this off the
      // response rather than the calculation id matters: the id is dispatched a beat earlier, so an
      // id comparison would seed from the previous response and then consider the new one seeded.
      hasSeededAdjustedAssignment: !!state.hasSeededAdjustedAssignment
        && nextAdjustedResponse === state.selectedStudy.calculationAdjustedResponse,
      isCalculateEnabled: true,
      sampleDataFresh: false,
      sampleDataLoading: false,
      sampleDataPendingLoads: null,
      hasNewInstrumentUploads: false,
      // The calculation ids produced here are sent on save, so results are an unsaved change.
      areStudyChangesPending: true
    };
  }),
  on(beginSampleDataLoad, (state): AppState => ({
    ...state,
    sampleDataLoading: true,
    sampleDataFresh: false,
    sampleDataPendingLoads: { awaitingAll: true, awaitingFailed: true }
  })),
  on(loadSampleDataSuccess, (state, { failedOnly, unadjustedSamples, adjustedSamples }): AppState => {
    const pending = state.sampleDataPendingLoads ?? { awaitingAll: false, awaitingFailed: false };
    const updatedPending = {
      awaitingAll: failedOnly ? pending.awaitingAll : false,
      awaitingFailed: failedOnly ? false : pending.awaitingFailed
    };
    const isComplete = !updatedPending.awaitingAll && !updatedPending.awaitingFailed;
    return {
      ...state,
      sampleDataLoading: isComplete ? false : true,
      sampleDataFresh: isComplete ? true : state.sampleDataFresh,
      sampleDataPendingLoads: isComplete ? null : updatedPending,
      selectedStudy: {
        ...state.selectedStudy,
        ...(failedOnly ? {
          sampleDataFailuresOriginal: cloneDeep(unadjustedSamples ?? []),
          sampleDataFailuresAdjusted: cloneDeep(adjustedSamples ?? [])
        } : {
          sampleDataOriginal: cloneDeep(unadjustedSamples ?? []),
          sampleDataAdjusted: cloneDeep(adjustedSamples ?? [])
        })
      }
    };
  }),
  on(loadSampleDataFailure, (state): AppState => ({
    ...state,
    sampleDataLoading: false,
    sampleDataFresh: false,
    sampleDataPendingLoads: null
  })),
  /** Adds or replaces a loaded calibrator lot and reevaluates calculation status. */
  on(loadCalLotSuccess, (state, { calLot, isRestore }): AppState => {
    const nextCalLots = upsertLot(state.selectedStudy.calLots ?? [], calLot, lot => lot.calLotNumber);
    const updatedStudy = { ...state.selectedStudy, calLots: nextCalLots };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      // Draft load rehydrates lots asynchronously, so only a user-driven import is an edit.
      areStudyChangesPending: isRestore ? state.areStudyChangesPending : true
    };
  }),
  /** Removes a calibration lot and prunes its vial data from adjusted lots. */
  on(deleteCalLot, (state, { lotNumber, assignmentVersion }): AppState => {
    const matchesRemovedCalLot = (lot: typeof state.selectedStudy.calLots[number]): boolean => {
      if (lot.calLotNumber !== lotNumber) {
        return false;
      }
      if (assignmentVersion === null || assignmentVersion === undefined) {
        return true;
      }
      return (lot.valueAssignmentVersion ?? null) === assignmentVersion;
    };
    const removedLot = state.selectedStudy.calLots.find(matchesRemovedCalLot);
    const remainingCalLots = state.selectedStudy.calLots.filter(lot => !matchesRemovedCalLot(lot));
    let nextAdjustedCalLots = state.selectedStudy.adjustedCalLots;

    if (remainingCalLots.length === 0) {
      nextAdjustedCalLots = { originalCalLot: [], adjustedCalLot: [], valueAssignment: [] };
    } else if (removedLot) {
      const removedBarcodes = new Set((removedLot.vials ?? []).map(vial => vial.barcode));
      const pruneVials = (lots: typeof state.selectedStudy.adjustedCalLots.originalCalLot) =>
        (lots ?? []).map(lot => {
          if (!lot?.vials || removedBarcodes.size === 0) {
            return lot;
          }
          const nextVials: { [barcode: string]: string } = {};
          Object.keys(lot.vials).forEach(barcode => {
            if (!removedBarcodes.has(barcode)) {
              nextVials[barcode] = lot.vials[barcode];
            }
          });
          return { ...lot, vials: nextVials };
        }).filter(lot => Object.keys(lot.vials ?? {}).length > 0);

      nextAdjustedCalLots = {
        ...state.selectedStudy.adjustedCalLots,
        originalCalLot: pruneVials(state.selectedStudy.adjustedCalLots.originalCalLot),
        adjustedCalLot: pruneVials(state.selectedStudy.adjustedCalLots.adjustedCalLot)
      };
    }

    const updatedStudy = { ...state.selectedStudy, calLots: remainingCalLots, adjustedCalLots: nextAdjustedCalLots };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      areStudyChangesPending: true
    };
  }),
  /** Sets regression mean, bias, and slope data. */
  on(setMeanBiasAndSlope, (state, { meanBiasAndSlope }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, meanBiasAndSlope: meanBiasAndSlope }
  })),
  /** Adds or replaces a loaded QC lot and reevaluates calculation status. */
  on(loadQcLotSuccess, (state, { qcLot, isRestore }): AppState => {
    const nextQcLots = upsertLot(state.selectedStudy.qcLots ?? [], qcLot, lot => lot.masterLotNumber);
    const updatedStudy = { ...state.selectedStudy, qcLots: nextQcLots };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      // Draft load rehydrates lots asynchronously, so only a user-driven import is an edit.
      areStudyChangesPending: isRestore ? state.areStudyChangesPending : true
    };
  }),
  /** Removes a QC lot and reevaluates calculation status. */
  on(deleteQcLot, (state, { lotNumber, assignmentVersion }): AppState => {
    const remainingQcLots = state.selectedStudy.qcLots.filter(lot => {
      if (lot.masterLotNumber !== lotNumber) {
        return true;
      }
      if (assignmentVersion === null || assignmentVersion === undefined) {
        return false;
      }
      return (lot.valueAssignmentVersion ?? null) !== assignmentVersion;
    });
    const updatedStudy = { ...state.selectedStudy, qcLots: remainingQcLots };
    return {
      ...state,
      selectedStudy: updatedStudy,
      isCalculateEnabled: enableCalculate(updatedStudy, state.panelName),
      areStudyChangesPending: true
    };
  }),
  /** Resets the entire state including authentication and study. */
  // Reset the entire store to a fresh app state (used on logout). Returns a clone of `initialState`
  // rather than a partial spread so every field — including `panelName`, `calculationStatus`, and a
  // fully-initialized `selectedStudy` — is reset, matching a from-scratch session and avoiding stale
  // in-memory state carrying over across the SPA logout/login cycle.
  on(resetAll, (): AppState => cloneDeep(initialState)),
  /** Sets the selected calculation report content item ID. */
  on(setSelectedCalcAssayLotRunId, (state, { reportContentItemId }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, selectedCalcReportContentItemId: reportContentItemId }
  })),
  /** Sets the selected sample report content item ID. */
  on(setSelectedSampleAssayLotRunId, (state, { reportContentItemId }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, selectedSampleReportContentItemId: reportContentItemId }
  })),
  /** Switches Calculations > Sample into or out of Failed Samples mode. */
  on(setCalcFailedSamplesMode, (state, { enabled }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, calcFailedSamplesMode: enabled }
  })),
  /** Switches Sample Analysis > List into or out of Failed Samples mode. */
  on(setSampleFailedSamplesMode, (state, { enabled }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, sampleFailedSamplesMode: enabled }
  })),
  /** Sets the adjusted calibrator lots. */
  on(setAdjustedCalLots, (state, { adjustedCalLots, isInitialLoad }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, adjustedCalLots: adjustedCalLots },
    // Rendering the tab counts as visited: an adjustment study must offer Calculate wADJ as soon
    // as the user opens Calibration, without having to edit a value first.
    adjustedCalibratorVisited: true,
    // Seeding the store as the tab renders is not an edit, though, so it must not block printing.
    areStudyChangesPending: isInitialLoad ? state.areStudyChangesPending : true
  })),
  /** Records that the current adjusted response has already seeded the Adjusted Calibrator form. */
  on(markAdjustedAssignmentSeeded, (state): AppState => ({
    ...state,
    hasSeededAdjustedAssignment: true
  })),
  /** Set the operator's override of the Allowed adjustment factor (%). */
  on(setAllowedAdjustmentFactor, (state, { allowedAdjustmentFactor }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, allowed_adjustment_factor: allowedAdjustmentFactor },
    areStudyChangesPending: true
  })),
  /** Set The order to display adjusted calibration lots according to the user-defined Run Report Items table */
  on(setCalAdjustedDisplayOrder, (state, { dataTableOrder }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, calAdjustedDisplayOrder: dataTableOrder }
  })),
  /** Resets study and panel selections. */
  on(resetSelectedStudyAndPanel, (state): AppState => ({
    ...state,
    selectedStudy: cloneDeep(initialState.selectedStudy),  // Reset to initial state
    panelName: initialState.panelName,
    existingStudy: false,
    isCalculateEnabled: false,
    isCalculateAdjustedEnabled: false,
    areStudyChangesPending: false,
    adjustedCalibratorVisited: false,
    calculationStatus: 'NA',
    loadStudyError: null,
    sampleDataFresh: false,
    sampleDataLoading: false,
    sampleDataPendingLoads: null,
    preparedInstrumentReportSeeds: [],
    preparedRunSampleLots: [],
    lastCalculationId: null,
    lastCalculationType: null,
    lastUnadjustedCalculationId: null,
    lastAdjustedCalculationId: null,
    lastSavedComment: null,
    calAdjustmentCriteria: [],
    hasNewInstrumentUploads: false,
    hasSeededAdjustedAssignment: false,
    calibratorCoaDetails: null,
    controlCoaDetails: null,
    // New Study leaves `studyId` at '' on both sides, so this is the only reliable
    // signal that the study changed — components keyed on it rebuild.
    studyInstanceId: (state.studyInstanceId ?? 0) + 1
  })),
  /** Enables or disables adjusted calculation. */
  on(setCalculateAdjustedEnabled, (state, { isCalculateAdjustedEnabled }): AppState => ({
    ...state,
    isCalculateAdjustedEnabled: isCalculateAdjustedEnabled
  })),
  /** Sets selected control QC analysis lot ID. */
  on(setSelectedControlQcAnalysisLotRunId, (state, { reportContentItemId }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, selectedControlAnalysisReportContentItemId: reportContentItemId }
  })),
  /** Sets selected control curve lot ID. */
  on(setSelectedControlCurveLotRunId, (state, { reportContentItemId }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, selectedControlCurveReportContentItemId: reportContentItemId }
  })),
  /** Sets the active assay tab for UI tracking. */
  on(setActiveAssayTab, (state, { activeAssayTab }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, activeAssayTab: activeAssayTab }
  })),
  /** Persists selected columns for the Assay Summary tab. */
  on(setAssaySummaryColumnSelection, (state, { columnKeys }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, assaySummaryColumnSelection: cloneDeep(columnKeys ?? []) }
  })),
  /** Persists selected columns for the Assay Details tab. */
  on(setAssayDetailsColumnSelection, (state, { columnKeys }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, assayDetailsColumnSelection: cloneDeep(columnKeys ?? []) }
  })),
  /** Records the Certificate of Analysis facts entered on the C of A screen. */
  on(setCoaDetails, (state, { coaDetails }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, coaDetails: cloneDeep(coaDetails) },
    // These are saved with the study, so entering one is an unsaved change like any other.
    areStudyChangesPending: true
  })),
  /** Remembers the calibrator CoA dialog's entries; not a study change, since they are not saved. */
  on(setCalibratorCoaDetails, (state, { calibratorCoaDetails }): AppState => ({
    ...state,
    calibratorCoaDetails: cloneDeep(calibratorCoaDetails)
  })),
  /** Remembers the control CoA dialog's entries; not a study change, since they are not saved. */
  on(setControlCoaDetails, (state, { controlCoaDetails }): AppState => ({
    ...state,
    controlCoaDetails: cloneDeep(controlCoaDetails)
  })),
  /** Updates study state on successful save operation. */
  on(saveStudySuccess, (state, { studyName, studyId, auditTrail, isCommitted }): AppState => ({
    ...state,
    selectedStudy: {
      ...state.selectedStudy,
      studyName: studyName,
      ...(studyId != null ? { studyId } : {}),
      ...(auditTrail ? { auditTrail } : {}),
      ...(isCommitted !== undefined ? { isCommitted } : {})
    },
    existingStudy: true,
    lastSavedComment: state.selectedStudy.comments ?? '',
    // A commit posts only the commit request, so it does not persist in-memory edits.
    areStudyChangesPending: isCommitted ? state.areStudyChangesPending : false
  })),
  /** Sets selected assignment report content item ID. */
  on(setSelectedAssignmentLotRunId, (state, { reportContentItemId }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, selectedAssignmentReportContentItemId: reportContentItemId }
  })),
  /** Adds or updates user comments on the study. */
  on(setComments, (state, { comments }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, comments: comments },
    areStudyChangesPending: true
  })),
  /** Sets the panel reference data for imported files. */
  on(setRunDataPanels, (state, { panelReference }): AppState => ({
    ...state,
    selectedStudy: { ...state.selectedStudy, runDataPanels: panelReference }
  })),
  /** Raises or clears the header's progress indicator, and records what it is reporting on. */
  on(setProcessingStatus, (state, { isProcessing, activity }): AppState => ({
    ...state,
    isProcessing: isProcessing,
    // Cleared alongside the flag: a stale activity would name whatever ran last.
    processingActivity: isProcessing ? activity : undefined,
    loadStudyError: isProcessing ? null : state.loadStudyError
  })),
  /** Sets the load study error message for header status display. */
  on(setLoadStudyError, (state, { message }): AppState => ({
    ...state,
    loadStudyError: message
  })),
  /** Stores the latest calculation cache identifiers for reuse on save. */
  on(setLastCalculationCache, (state, { calculationId, calculationType, clearUnadjusted }): AppState => ({
    ...state,
    lastCalculationId: calculationId,
    lastCalculationType: calculationType,
    lastUnadjustedCalculationId: calculationType === 'unadjusted'
      ? calculationId
      : clearUnadjusted ? null : state.lastUnadjustedCalculationId,
    lastAdjustedCalculationId: calculationType === 'adjusted' ? calculationId : state.lastAdjustedCalculationId
  })),
  /** Marks that new instrument files have been uploaded in this session, preventing study-bucket calc lookup. */
  on(setHasNewInstrumentUploads, (state, { hasNewInstrumentUploads }): AppState => ({
    ...state,
    hasNewInstrumentUploads
  })),
  /** Replaces the study's audit trail with the latest data from the server. */
  on(setAuditTrail, (state, { auditTrail }): AppState => ({
    ...state,
    selectedStudy: {
      ...state.selectedStudy,
      auditTrail
    }
  })),
);
